import React from "react";
import { Form } from "react-bootstrap";
import type { ConversionRuleField } from "@models/conversionRuleSchema.model";
import type { Rule } from "./ConversionRuleBuilder";

interface Props {
  rule: Rule;
  fieldType: string;
  selectedField: ConversionRuleField | undefined;
  onUpdate: (ruleId: string, updates: Partial<Rule>) => void;
}

const RuleValueInput: React.FC<Props> = ({ rule, fieldType, selectedField, onUpdate }) => {
  if (!rule.operator || rule.operator === "is_null" || rule.operator === "is_not_null") {
    return <Form.Control disabled placeholder="No value needed" style={{ width: 200 }} size="sm" />;
  }

  // "is one of" a catalog value group (MPN-2328): one named list, kept in the catalog,
  // instead of codes typed here. A stored group the schema no longer offers stays
  // visible and flagged rather than silently reading as unselected.
  const groups = selectedField?.value_groups ?? [];
  if (rule.operator === "in" && (groups.length > 0 || rule.value_group)) {
    const unlisted = rule.value_group && !groups.some((g) => g.code === rule.value_group);
    return (
      <Form.Select
        value={rule.value_group ?? ""}
        isInvalid={Boolean(unlisted)}
        onChange={(e) => onUpdate(rule.id, { value_group: e.target.value || undefined, value: null })}
        title="Value group"
        style={{ width: 200 }}
        size="sm"
      >
        <option value="">Select a group...</option>
        {unlisted && (
          <option value={rule.value_group} disabled>
            {`${rule.value_group} — not available`}
          </option>
        )}
        {groups.map((g) => (
          <option key={g.code} value={g.code}>
            {g.name}
          </option>
        ))}
      </Form.Select>
    );
  }

  if (fieldType === "boolean") {
    return (
      <Form.Select
        value={String(rule.value)}
        onChange={(e) => onUpdate(rule.id, { value: e.target.value === "true" })}
        style={{ width: 200 }}
        size="sm"
      >
        <option value="">Select...</option>
        <option value="true">True</option>
        <option value="false">False</option>
      </Form.Select>
    );
  }

  if (fieldType === "choice" && selectedField?.choices) {
    if (rule.operator === "in") {
      return (
        <Form.Select
          multiple
          value={rule.value || []}
          onChange={(e) => {
            const values = Array.from(e.target.selectedOptions, (option) => option.value);
            onUpdate(rule.id, { value: values });
          }}
          style={{ width: 200, height: 80 }}
          size="sm"
        >
          {selectedField.choices.map((choice) => (
            <option key={choice.value} value={choice.value}>
              {choice.label}
            </option>
          ))}
        </Form.Select>
      );
    }
    return (
      <Form.Select
        value={rule.value || ""}
        onChange={(e) => onUpdate(rule.id, { value: e.target.value })}
        style={{ width: 200 }}
        size="sm"
      >
        <option value="">Select...</option>
        {selectedField.choices.map((choice) => (
          <option key={choice.value} value={choice.value}>
            {choice.label}
          </option>
        ))}
      </Form.Select>
    );
  }

  if (fieldType === "date") {
    return (
      <Form.Control
        type="date"
        value={rule.value || ""}
        onChange={(e) => onUpdate(rule.id, { value: e.target.value })}
        style={{ width: 200 }}
        size="sm"
      />
    );
  }

  if (fieldType === "number") {
    return (
      <Form.Control
        type="number"
        value={rule.value || ""}
        onChange={(e) => onUpdate(rule.id, { value: parseFloat(e.target.value) })}
        style={{ width: 200 }}
        placeholder="Enter number..."
        size="sm"
      />
    );
  }

  return (
    <Form.Control
      type="text"
      value={rule.value || ""}
      onChange={(e) => onUpdate(rule.id, { value: e.target.value })}
      style={{ width: 200 }}
      placeholder="Enter value..."
      size="sm"
    />
  );
};

export default RuleValueInput;
